import { MealType } from '@akin-home/amplify';
import { notNullOrUndefined } from '@akin-home/arrays';
import { MealPlanPreferenceStateVariableNames as PreferenceVariables } from '@akin-home/meals';
import { AllergyText, MealPlanPreferences } from '@akin-home/recipes';
import {
  StateUpdate,
  updateStateVariables,
} from '@akin-home/state-variables-frontend';

interface Params {
  application_id: number;
  username: string;
  preferences: MealPlanPreferences;
}

export const saveMealPreferencesToStateVariables = async ({
  application_id,
  username,
  preferences,
}: Params) => {
  const { allergies, meals_to_plan, meats_eaten, meal_times, reminders } =
    preferences;
  let updates: StateUpdate[] = [];

  /** Utility function, since all updates will have the same operator and value_type. Takes a var_name and value pair and adds to updates array */
  const update = (...pairs: [string, string][]) => {
    const variable_updates: StateUpdate[] = pairs.map(([var_name, value]) => ({
      operator: '=',
      value_type: 'primitive',
      var_name,
      value,
    }));

    updates = updates.concat(variable_updates);
  };

  update(
    [PreferenceVariables.BUDGET, `${preferences.budget ?? 2}`],
    [PreferenceVariables.PEOPLE, `${preferences.people ?? 1}`],
    [PreferenceVariables.DIET_TYPE, `${preferences.dietary_balance ?? 2}`],
    [PreferenceVariables.DIFFICULTY, `${preferences.time_and_effort ?? 2}`],

    /** Allergies */
    [
      PreferenceVariables.DAIRY_ALLERGY,
      allergies?.includes(AllergyText.DAIRY) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.EGG_ALLERGY,
      allergies?.includes(AllergyText.EGGS) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.PEANUT_ALLERGY,
      allergies?.includes(AllergyText.PEANUTS) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.SESAME_ALLERGY,
      allergies?.includes(AllergyText.SESAME) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.SHELLFISH_ALLERGY,
      allergies?.includes(AllergyText.SHELLFISH) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.SOY_ALLERGY,
      allergies?.includes(AllergyText.SOY) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.TREENUT_ALLERGY,
      allergies?.includes(AllergyText.TREE_NUTS) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.WHEAT_ALLERGY,
      allergies?.includes(AllergyText.WHEAT) ? 'true' : 'false',
    ],
    /** Meat eating */
    [
      PreferenceVariables.EATS_BEEF,
      meats_eaten?.includes('beef') ? 'true' : 'false',
    ],
    [
      PreferenceVariables.EATS_LAMB,
      meats_eaten?.includes('lamb') ? 'true' : 'false',
    ],
    [
      PreferenceVariables.EATS_PORK,
      meats_eaten?.includes('pork') ? 'true' : 'false',
    ],
    [
      PreferenceVariables.EATS_CHICKEN,
      meats_eaten?.includes('chicken') ? 'true' : 'false',
    ],
    [
      PreferenceVariables.EATS_FISH,
      meats_eaten?.includes('fish') ? 'true' : 'false',
    ],
    [
      PreferenceVariables.EATS_TURKEY,
      meats_eaten?.includes('turkey') ? 'true' : 'false',
    ],

    /** Meals to plan */
    [
      PreferenceVariables.PLAN_BREAKFAST,
      meals_to_plan?.includes(MealType.Breakfast) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.PLAN_LUNCH,
      meals_to_plan?.includes(MealType.Lunch) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.PLAN_DINNER,
      meals_to_plan?.includes(MealType.Dinner) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.PLAN_SNACK,
      meals_to_plan?.includes(MealType.Snack) ? 'true' : 'false',
    ],

    /** Meal times */
    [PreferenceVariables.BREAKFAST_TIME, meal_times?.Breakfast || ''],
    [PreferenceVariables.LUNCH_TIME, meal_times?.Lunch || ''],
    [PreferenceVariables.DINNER_TIME, meal_times?.Dinner || ''],
    [PreferenceVariables.SNACK_TIME, meal_times?.Snack || ''],

    /** Reminders */
    [
      PreferenceVariables.BREAKFAST_REMINDER,
      reminders?.includes(MealType.Breakfast) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.LUNCH_REMINDER,
      reminders?.includes(MealType.Lunch) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.DINNER_REMINDER,
      reminders?.includes(MealType.Dinner) ? 'true' : 'false',
    ],
    [
      PreferenceVariables.SNACK_REMINDER,
      reminders?.includes(MealType.Snack) ? 'true' : 'false',
    ]
  );

  if (notNullOrUndefined(preferences.eats_meat)) {
    update([
      PreferenceVariables.EATS_MEAT,
      preferences.eats_meat ? 'true' : 'false',
    ]);
  }

  return await updateStateVariables({ application_id, username, updates });
};
